import { useState } from "react";
import {
  User,
  Settings,
  Bell,
  HelpCircle,
  LogOut,
  ChevronRight,
  Award,
  Info,
} from "lucide-react";
import welcomeBg from "@/projects/Admin_Bite_Balance/bite-balance/src/assets/welcome-bg.jpg";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/avatar";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/popover";
import { useNavigate } from "react-router-dom";

const Profile = () => {
  const navigate = useNavigate();
  const userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}");
  const profileImage = localStorage.getItem("profileImage");
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("userProfile");
    localStorage.removeItem("onboardingComplete");
    navigate("/");
  };

  const menuSections = [
    {
      title: "App Settings",
      items: [
        {
          icon: Bell,
          label: "Notifications",
          color: "text-chart-4",
          bgColor: "bg-info-light",
        },
        {
          icon: Settings,
          label: "Preferences",
          color: "text-muted-foreground",
          bgColor: "bg-muted",
        },
      ],
    },
    {
      title: "Support",
      items: [
        {
          icon: HelpCircle,
          label: "Help & Support",
          color: "text-muted-foreground",
          bgColor: "bg-muted",
        },
        {
          icon: Award,
          label: "About Bite Balance",
          color: "text-muted-foreground",
          bgColor: "bg-muted",
        },
      ],
    },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden pb-24">
      {/* Blurred Background Image */}
      <div
        className="fixed inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{ backgroundImage: `url(${welcomeBg})` }}
      />

      {/* Overlay for better contrast */}
      <div className="fixed inset-0 bg-background/70" />

      <div className="relative z-10 mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="pt-2">
          <h1 className="text-2xl font-bold text-foreground">Profile</h1>
          <p className="text-sm text-muted-foreground">
            Manage your account & preferences
          </p>
        </div>

        {/* User Profile Card */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-4">
            <Avatar
              className="h-16 w-16 cursor-pointer border-4 border-primary/10 transition-opacity hover:opacity-80"
              onClick={() => navigate("/profile/photo")}
            >
              {profileImage ? (
                <AvatarImage src={profileImage} alt="Profile" />
              ) : (
                <AvatarFallback className="to-chart-1 bg-gradient-to-br from-primary text-xl font-bold text-white">
                  {userProfile.name
                    ?.split(" ")
                    .map((n: string) => n[0])
                    .join("") || "RS"}
                </AvatarFallback>
              )}
            </Avatar>

            <div className="flex-1">
              <h3 className="text-lg font-bold text-foreground">
                {userProfile.name || "Rajesh Shah"}
              </h3>
              <p className="text-sm text-muted-foreground">
                {userProfile.email || "rajesh@gmail.com"}
              </p>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="h-9 w-full rounded-lg"
            onClick={() => navigate("/profile/edit")}
          >
            Edit Profile
          </Button>
        </Card>

        {/* Stats Overview */}
        <div className="grid grid-cols-3 gap-2">
          <Card className="rounded-xl border-0 bg-gradient-to-br from-primary/5 to-primary/10 p-3 text-center shadow-sm">
            <div className="text-xl font-bold text-foreground">
              {userProfile.weightUnit === "lbs"
                ? `${userProfile.weightLbs || "150"}lbs`
                : `${userProfile.weightKg || "68"}kg`}
            </div>
            <div className="text-xs text-muted-foreground">Current</div>
            {userProfile.targetWeight && (
              <div className="mt-1 text-xs font-medium text-primary">
                Target: {userProfile.targetWeight}
                {userProfile.targetWeightUnit || "kg"}
              </div>
            )}
          </Card>
          <Card className="rounded-xl border-0 bg-gradient-to-br from-secondary/5 to-secondary/10 p-3 text-center shadow-sm">
            <div className="text-xl font-bold text-foreground">
              {userProfile.height || "170"}cm
            </div>
            <div className="text-xs text-muted-foreground">Height</div>
            <div className="mt-1 text-xs font-medium text-secondary">
              {userProfile.gender || "Male"}
            </div>
          </Card>
          <Card className="rounded-xl border-0 bg-gradient-to-br from-accent/5 to-accent/10 p-3 text-center shadow-sm">
            <div className="text-xl font-bold text-foreground">
              {userProfile.age || "26"}
            </div>
            <div className="text-xs text-muted-foreground">Years</div>
            <div className="mt-1 text-xs font-medium text-accent">Age</div>
          </Card>
        </div>

        {/* BMI Card */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-bold text-foreground">
              Body Mass Index (BMI)
            </h3>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Info className="h-4 w-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 border-2 p-4">
                <div className="space-y-3">
                  <h4 className="text-sm font-bold">How BMI is Calculated</h4>
                  <p className="text-xs font-semibold text-muted-foreground">
                    BMI = weight (kg) / height² (m²)
                  </p>
                  <div className="space-y-2 text-xs">
                    <p className="font-bold">BMI Categories:</p>
                    <div className="space-y-1 font-semibold text-muted-foreground">
                      <p>• Underweight: &lt; 18.5</p>
                      <p>• Normal weight: 18.5 - 24.9</p>
                      <p>• Overweight: 25 - 29.9</p>
                      <p>• Obese: ≥ 30</p>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex items-center justify-center">
            <div className="text-center">
              <div className="mb-2 text-5xl font-bold text-primary">22.4</div>
              <div className="inline-block rounded-xl border-2 border-green-500/30 bg-green-500/10 px-4 py-2">
                <p className="text-sm font-bold text-green-600">
                  Normal Weight
                </p>
              </div>
              <p className="mt-3 text-xs font-semibold text-muted-foreground">
                Based on height: 170cm, weight: 65kg
              </p>
            </div>
          </div>
        </Card>

        {/* Goals Card */}
        <Card
          className="cursor-pointer rounded-2xl border-0 p-5 shadow-sm transition-shadow hover:shadow-md"
          onClick={() => navigate("/profile/goals")}
        >
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-semibold text-foreground">Your Goals</h3>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">
                Weight Loss Goal
              </span>
              <span className="font-medium text-foreground">
                {userProfile.weightLossGoal || "Not Sure"}
              </span>
            </div>
            <div className="h-px bg-border" />

            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">
                Activity Level
              </span>
              <span className="font-medium text-foreground">
                {userProfile.activityLevel || "Moderately active"}
              </span>
            </div>
            <div className="h-px bg-border" />

            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">Diet Type</span>
              <span className="font-medium text-foreground">
                {userProfile.dietType || "Classic"}
              </span>
            </div>
            <div className="h-px bg-border" />

            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">
                Fasting Window
              </span>
              <span className="max-w-[60%] text-right font-medium text-foreground">
                {userProfile.intermittentFasting ===
                "Yes — I follow a fasting window"
                  ? userProfile.fastingWindow || "16:8"
                  : userProfile.intermittentFasting ||
                    "No — I eat throughout the day"}
              </span>
            </div>
          </div>
        </Card>

        {/* Menu Sections */}
        {menuSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="space-y-2">
            <h4 className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {section.title}
            </h4>
            <Card className="overflow-hidden rounded-2xl border-0 shadow-sm">
              {section.items.map((item, index) => {
                const Icon = item.icon;
                const routes: Record<string, string> = {
                  "Personal Information": "/profile/personal-info",
                  "Goals & Preferences": "/profile/goals",
                  Notifications: "/profile/notifications",
                  Preferences: "/profile/preferences",
                  "Help & Support": "/profile/help",
                  "About Bite Balance": "/profile/about",
                };
                return (
                  <div key={index}>
                    <button
                      className="flex w-full items-center gap-4 p-4 transition-colors hover:bg-muted/50"
                      onClick={() => {
                        const route = routes[item.label];
                        if (route) {
                          navigate(route);
                        }
                      }}
                    >
                      <div
                        className={`h-10 w-10 rounded-xl ${item.bgColor} flex items-center justify-center`}
                      >
                        <Icon className={`h-5 w-5 ${item.color}`} />
                      </div>
                      <span className="flex-1 text-left font-medium text-foreground">
                        {item.label}
                      </span>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </button>
                    {index < section.items.length - 1 && (
                      <div className="mx-4 h-px bg-border" />
                    )}
                  </div>
                );
              })}
            </Card>
          </div>
        ))}

        {/* Logout Button */}
        <Button
          onClick={() => setShowLogoutDialog(true)}
          variant="outline"
          className="h-12 w-full rounded-2xl border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <LogOut className="mr-2 h-5 w-5" />
          Logout
        </Button>

        {/* Logout Confirmation Dialog */}
        <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Logout Confirmation</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to logout? You'll need to sign in again to
                access your account.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleLogout}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Logout
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* App Version */}
        <p className="text-center text-xs text-muted-foreground">
          Bite Balance v1.0.0
        </p>
      </div>
    </div>
  );
};

export default Profile;
